import {
    InputLabel,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { DialogWindow } from "UI/DialogWindow";
import { EditActionButtons } from "UI/EditActionButtons";
import { FormTextField } from "UI/FormTextField";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useEditItem } from "hooks/useEditItem";
import { required } from "models/validations";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { ModalSpinner } from "./ModalSpinner";

const defaultFormData = {
  id: "",
  name: "",
  speciality: "",
  practiceId: "",
};

export const EditProviderDialog = ({
  open,
  practiceId,
  providerId,
  handleClose,
  refreshList,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning, openConfirmationWithCallback, userData, loadUserData } =
    useContext(GlobalContext);
  const [isLoading, setIsLoading] = useState(true);

  const {
    previousData,
    setPreviousData,
    control,
    getValues,
    handleSubmit,
    isDirty,
    reset,
    handleCancel,
    watch,
  } = useEditItem({ defaultFormData, handleClose });

  const deleteProvider = createApiCall(async () => {
    const provider = getValues();
    const body = {
      provider,
    };

    const result = await API.del("restApi", `/providers`, {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Provider deleted successfully
          </Typography>
        </Stack>
      ),
      content: <></>,
    });

    refreshList();
    handleClose();
  });

  const handleDelete = () => {
    openConfirmationWithCallback({
      title: "Warning",
      message:
        "This provider will be deleted. Proceed?",
      callbackFn: () => {
        deleteProvider();
      },
      buttonLabels: ["No", "Yes"],
    });
  };

  const onSubmit = createApiCall(async () => {
    const provider = getValues();

    const result = await API.post("restApi", "/providers", {
      body: {
        provider,
        action: "update",
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    refreshList();
    handleClose();
    loadUserData(userData?.cognitoId);
  });

  useEffect(() => {
    const loadData = createApiCall(
      async () => {
        setIsLoading(true);
        const result = await API.get(
          "restApi",
          `/providers/${providerId}`,
          {}
        );
        const data = result ?? defaultFormData;
        reset(data);
        setPreviousData(data);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );

    if (providerId && open) {
      loadData();
    } else {
      reset(defaultFormData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, open]);

  const isSuperAdmin = userData?.role === "Super Admin" || userData?.email === "admin@referral-stream.com";
  const creatorRecipientId = watch("creatorRecipientId");

  const isPracticeEditable =
    isSuperAdmin ||
    userData?.organizationId === creatorRecipientId ||
    practiceId === userData?.organizationId;

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={handleCancel}
      title={"Edit Provider"}
      content={
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              direction={fullScreen ? "column" : "row"}
              gap={3}
              mt={2}
              maxWidth={"480px"}
            >
              <Stack width="100%" gap={2}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="name">Name</InputLabel>
                  <FormTextField
                    id="name"
                    name="name"
                    control={control}
                    rules={{ required }}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={!isPracticeEditable}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="speciality">Speciality</InputLabel>
                  <FormTextField
                    id="speciality"
                    name="speciality"
                    control={control}
                    rules={{ required }}
                    InputProps={{
                      autoComplete: "off",
                    }}
                    disabled={!isPracticeEditable}
                  />
                </Stack>
              </Stack>
            </Stack>
          </form>
          {isLoading && <ModalSpinner open />}
        </>
      }
      actions={
        <EditActionButtons
          handleCancel={handleCancel}
          handleDelete={handleDelete}
          isDeleteButtonPresent={true}
          previousData={previousData}
          isDirty={isDirty}
          reset={reset}
          saveChanges={handleSubmit(onSubmit)}
        />
      }
    />
  );
};
